import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Flag, Calendar, AlertCircle, Activity, TrendingUp } from 'lucide-react'
import { adminApi } from '../lib/api'
import { transformData } from '../lib/transformers'
import type { Stats } from '../types'

export default function DashboardPage() {
  const [stats, setStats] = useState<Stats | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const response = await adminApi.getStats()
      console.log('Stats API response:', response.data)
      
      // Extract stats from nested structure
      let rawData = response.data
      if (response.data?.data) {
        rawData = response.data.data
      }
      
      // The API returns stats in a nested 'stats' object
      const apiStats = rawData?.stats || rawData
      
      console.log('Extracted stats data:', apiStats)
      
      // Transform snake_case to camelCase and map to our Stats interface
      const transformedStats = transformData<any>(apiStats)
      
      // Map API fields to our expected Stats interface
      const mappedStats: Stats = {
        totalUsers: transformedStats.totalUsers || 0,
        activeUsers: transformedStats.activeUsers || 0,
        totalTrips: transformedStats.totalTrips || 0,
        totalEvents: transformedStats.totalEvents || 0,
        pendingModeration: transformedStats.pendingModeration || 0,
        todayActions: rawData?.recent_activity?.length || 0,
      }
      
      console.log('Mapped stats:', mappedStats)
      
      setStats(mappedStats)
    } catch (error) {
      console.error('Failed to load stats:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  const statCards = [
    {
      name: 'Total Users',
      value: stats?.totalUsers || 0,
      icon: Users,
      link: '/users',
    },
    {
      name: 'Active Users',
      value: stats?.activeUsers || 0,
      icon: Activity,
      link: '/users',
    },
    {
      name: 'Total Trips',
      value: stats?.totalTrips || 0,
      icon: Flag,
      link: '/trips',
    },
    {
      name: 'Total Events',
      value: stats?.totalEvents || 0,
      icon: Calendar,
      link: '/events',
    },
    {
      name: 'Pending Moderation',
      value: stats?.pendingModeration || 0,
      icon: AlertCircle,
      link: '/moderation',
    },
    {
      name: 'Today\'s Actions',
      value: stats?.todayActions || 0,
      icon: TrendingUp,
      link: '/audit-log',
    },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Overview of your admin panel</p>
      </div>

      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {statCards.map((stat) => (
          <Link
            key={stat.name}
            to={stat.link}
            className="card hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-lg p-3" style={{ backgroundColor: '#97DF06' }}>
                <stat.icon className="h-6 w-6 text-black" />
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-2xl font-semibold text-gray-900">{stat.value}</p>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="space-y-2">
            <Link
              to="/trips/new"
              className="block p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Create New Trip</p>
              <p className="text-sm text-gray-600">Add a trip as Waybook official account</p>
            </Link>
            <Link
              to="/events/new"
              className="block p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Create New Event</p>
              <p className="text-sm text-gray-600">Add an event as Waybook official account</p>
            </Link>
            <Link
              to="/moderation"
              className="block p-3 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <p className="font-medium text-gray-900">Review Moderation Queue</p>
              <p className="text-sm text-gray-600">
                {stats?.pendingModeration || 0} items pending review
              </p>
            </Link>
          </div>
        </div>

        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
          <div className="space-y-3">
            <div className="text-sm text-gray-600 text-center py-8">
              No recent activity
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
